import { Text, View } from '@react-pdf/renderer';

import styles from './TableOfContentStyles.ts';

const getFirstLevelTitle = (id: string, pages: PageData[]) => {
  if (id.startsWith('lamp-info-0')) {
    // Find the first lamp info page to get its chapter number prefix
    const lampPage = pages.find((p) => p.id.startsWith('lamp-info-0'));
    const chapterNumber = lampPage ? lampPage.chapterNumber.split('.')[0] + '.' : '3.';
    return {
      firstKey: 'lamp-info',
      firstTitle: 'Luminaire Information',
      firstChapterNumber: chapterNumber,
    };
  }
  if (id.startsWith('graphs-1')) {
    // Find the first graphs page to get its chapter number prefix
    const graphPage = pages.find((p) => p.id.startsWith('graphs-1'));
    const chapterNumber = graphPage ? graphPage.chapterNumber.split('.')[0] + '.' : '5.';
    return {
      firstKey: 'graphs',
      firstTitle: 'Layer Plots',
      firstChapterNumber: chapterNumber,
    };
  }
  return null;
};

interface PageData {
  id: string;
  title: string;
  chapterNumber: string;
}

interface TableOfContentProps {
  finalPages: PageData[];
  pagesData: PageData[];
  pageNumbers: Record<string, number>;
}

const TableOfContent = ({ finalPages, pagesData, pageNumbers }: TableOfContentProps) => {
  const hasCoverPage = finalPages.some((page) => page.id === 'cover-page');
  const hasTableOfContent = finalPages.some((page) => page.id === 'table-of-content');

  const coverPage = hasCoverPage ? finalPages.find((page) => page.id === 'cover-page') : null;
  const tableOfContent = hasTableOfContent ? finalPages.find((page) => page.id === 'table-of-content') : null;

  return (
    <View style={styles.container}>
      <Text style={styles.titleContent}>Table of Contents</Text>

      {coverPage && (
        <View key={coverPage.id} style={styles.titleLine}>
          <Text style={styles.numberOfSectionTitleLine}>{coverPage.chapterNumber}</Text>
          <Text style={styles.titleOfPage}>{coverPage.title}</Text>
          <Text style={styles.numberOfPage}>1</Text>
        </View>
      )}

      {tableOfContent && (
        <View key={tableOfContent.id} style={styles.titleLine}>
          <Text style={styles.numberOfSectionTitleLine}>{tableOfContent.chapterNumber}</Text>
          <Text style={styles.titleOfPage}>{tableOfContent.title}</Text>
          <Text style={styles.numberOfPage}>2</Text>
        </View>
      )}

      {pagesData.map((page, _index) => {
        const firstLevelTitle = getFirstLevelTitle(page.id, pagesData);
        const pageNumber = pageNumbers[page.id] || '?';

        const dotCount = (page.chapterNumber.match(/\./g) || []).length;
        const isFirstLevel = dotCount <= 1;

        return (
          <View key={page.id}>
            {firstLevelTitle && (
              <View key={firstLevelTitle.firstKey} style={styles.titleLine}>
                <Text style={styles.numberOfSectionTitleLine}>{firstLevelTitle.firstChapterNumber}</Text>
                <Text style={styles.titleOfPage}>{firstLevelTitle.firstTitle}</Text>
                <Text style={styles.numberOfPage}>{pageNumber}</Text>
              </View>
            )}

            <View style={isFirstLevel ? styles.titleLine : styles.row}>
              <Text style={isFirstLevel ? styles.numberOfSectionTitleLine : styles.numberOfSection}>
                {page.chapterNumber}
              </Text>
              <Text style={styles.titleOfPage}>{page.title}</Text>
              <Text style={styles.numberOfPage}>{pageNumber}</Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

export default TableOfContent;
